import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { MapPin, KeyRound, CheckCircle2, XCircle, Loader2, Save, Plug } from "lucide-react";

const PURPLE = "#2B1E4A";
const MAGENTA = "#B5179E";
const GOLD = "#F5B400";

const STATUS = {
  active: ["Conectado", "bg-emerald-50 text-emerald-700", CheckCircle2],
  invalid: ["Chave inválida", "bg-red-50 text-red-700", XCircle],
  untested: ["Não testado", "bg-amber-50 text-amber-700", Plug],
  unconfigured: ["Não configurado", "bg-gray-100 text-gray-500", Plug],
};

export default function LogisticaMapsConfig() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [form, setForm] = useState({ browser_api_key: "", server_api_key: "" });
  const [origin, setOrigin] = useState({ default_origin_address: "", default_origin_lat: "", default_origin_lng: "" });
  const [flags, setFlags] = useState({ enabled: false, return_to_origin: true, traffic_aware: false });

  const load = () => api.get("/deliveries/maps/config").then(({ data }) => {
    setCfg(data);
    setFlags({ enabled: !!data.enabled, return_to_origin: !!data.return_to_origin, traffic_aware: !!data.traffic_aware });
    setOrigin({
      default_origin_address: data.default_origin_address || "",
      default_origin_lat: data.default_origin_lat ?? "",
      default_origin_lng: data.default_origin_lng ?? "",
    });
  }).catch(() => toast.error("Erro ao carregar configuração"));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...flags, ...origin };
      if (form.browser_api_key.trim()) payload.browser_api_key = form.browser_api_key.trim();
      if (form.server_api_key.trim()) payload.server_api_key = form.server_api_key.trim();
      const { data } = await api.put("/deliveries/maps/config", payload);
      setCfg(data);
      setForm({ browser_api_key: "", server_api_key: "" });
      toast.success("Configuração salva");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/deliveries/maps/test");
      if (data.ok) toast.success(data.message); else toast.error(data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro no teste"); }
    finally { setTesting(false); }
  };

  if (!cfg) return <div className="p-8 text-center text-sm text-[#9A8A98]">Carregando...</div>;
  const [stLabel, stCls, StIcon] = STATUS[cfg.status] || STATUS.unconfigured;

  return (
    <div className="mx-auto max-w-2xl space-y-5 p-4 lg:p-8" data-testid="maps-config-page">
      <div className="flex flex-wrap items-center gap-3 rounded-2xl p-5 text-white" style={{ background: `linear-gradient(135deg, ${PURPLE}, ${MAGENTA})` }}>
        <span className="grid h-11 w-11 place-items-center rounded-xl" style={{ background: GOLD }}><MapPin size={22} className="text-[#2B1E4A]" /></span>
        <div className="flex-1"><div className="text-[11px] font-bold uppercase tracking-[0.25em]" style={{ color: GOLD }}>CRIS ROTA · Configurações</div><h1 className="font-display text-lg font-bold">Google Maps</h1></div>
        <span className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${stCls}`} data-testid="maps-status"><StIcon size={13} /> {stLabel}</span>
      </div>

      {/* Integração */}
      <div className="space-y-4 rounded-2xl border border-border bg-white p-5">
        <label className="flex items-center justify-between">
          <span className="text-sm font-bold text-brand-dark">Integração ativa</span>
          <input type="checkbox" checked={flags.enabled} onChange={(e) => setFlags({ ...flags, enabled: e.target.checked })} className="h-5 w-5" data-testid="maps-enabled-toggle" />
        </label>

        <div>
          <label className="mb-1 flex items-center gap-1 text-[12px] font-bold text-[#6B5A69]"><KeyRound size={13} /> Chave de Navegador (browser)</label>
          <input value={form.browser_api_key} onChange={(e) => setForm({ ...form, browser_api_key: e.target.value })}
            placeholder={cfg.has_browser_key ? `Atual: ${cfg.browser_masked} · deixe vazio para manter` : "Cole a browser key"}
            className="w-full rounded-xl border border-border px-3 py-2 text-sm" data-testid="maps-browser-key" />
          <p className="mt-1 text-[11px] text-[#9A8A98]">Usada no mapa (frontend). Restrinja por referrer no Google Cloud.</p>
        </div>

        <div>
          <label className="mb-1 flex items-center gap-1 text-[12px] font-bold text-[#6B5A69]"><KeyRound size={13} /> Chave de Servidor (server)</label>
          <input type="password" value={form.server_api_key} onChange={(e) => setForm({ ...form, server_api_key: e.target.value })}
            placeholder={cfg.has_server_key ? `Atual: ${cfg.server_masked} · deixe vazio para manter` : "Cole a server key"}
            className="w-full rounded-xl border border-border px-3 py-2 text-sm" data-testid="maps-server-key" />
          <p className="mt-1 text-[11px] text-[#9A8A98]">Armazenada criptografada. Nunca é exibida em texto puro nem enviada ao navegador.</p>
        </div>

        {cfg.last_validated_at && <p className="text-[11px] text-[#8A7788]">Última validação: {new Date(cfg.last_validated_at).toLocaleString("pt-BR")}</p>}
      </div>

      {/* Origem padrão */}
      <div className="space-y-3 rounded-2xl border border-border bg-white p-5">
        <h2 className="text-sm font-bold text-brand-dark">Origem padrão das rotas (opcional)</h2>
        <input value={origin.default_origin_address} onChange={(e) => setOrigin({ ...origin, default_origin_address: e.target.value })} placeholder="Endereço da base (ex.: Av. Paulista 1000, São Paulo)" className="w-full rounded-xl border border-border px-3 py-2 text-sm" data-testid="maps-origin-address" />
        <div className="grid grid-cols-2 gap-2">
          <input value={origin.default_origin_lat} onChange={(e) => setOrigin({ ...origin, default_origin_lat: e.target.value })} placeholder="Latitude" className="rounded-xl border border-border px-3 py-2 text-sm" data-testid="maps-origin-lat" />
          <input value={origin.default_origin_lng} onChange={(e) => setOrigin({ ...origin, default_origin_lng: e.target.value })} placeholder="Longitude" className="rounded-xl border border-border px-3 py-2 text-sm" data-testid="maps-origin-lng" />
        </div>
        <label className="flex items-center justify-between"><span className="text-[13px] font-semibold text-[#6B5A69]">Retornar à base no fim da rota</span><input type="checkbox" checked={flags.return_to_origin} onChange={(e) => setFlags({ ...flags, return_to_origin: e.target.checked })} className="h-5 w-5" data-testid="maps-return-toggle" /></label>
        <label className="flex items-center justify-between"><span className="text-[13px] font-semibold text-[#6B5A69]">Considerar trânsito (traffic aware)</span><input type="checkbox" checked={flags.traffic_aware} onChange={(e) => setFlags({ ...flags, traffic_aware: e.target.checked })} className="h-5 w-5" data-testid="maps-traffic-toggle" /></label>
      </div>

      <div className="flex gap-2">
        <button onClick={save} disabled={saving} className="flex flex-1 items-center justify-center gap-1 rounded-xl py-3 text-sm font-bold text-white disabled:opacity-50" style={{ background: PURPLE }} data-testid="maps-save-btn">{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Salvar</button>
        <button onClick={test} disabled={testing} className="flex flex-1 items-center justify-center gap-1 rounded-xl border border-border py-3 text-sm font-bold text-[#5B2CA0] disabled:opacity-50" data-testid="maps-test-btn">{testing ? <Loader2 size={16} className="animate-spin" /> : <Plug size={16} />} Testar conexão</button>
      </div>
    </div>
  );
}
